import { useEffect, useRef, useState } from 'react';
import {
    MAX_TOTAL_RECORDS,
    MIN_TOTAL_RECORDS,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/constants';
import { maxRecordsErrorText } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/RecordCountInput';
import { createDefaultTargetConfig } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/steps/RecordsConfigStep';

export const minRecordsErrorText = (min) => `Minimum records: ${min.toLocaleString('en-US')}`;

/** Sum of the finite per-target counts (an empty input counts as 0). */
export const sumCounts = (counts) =>
    Object.values(counts || {}).reduce((sum, value) => sum + (Number.isFinite(value) ? value : 0), 0);

/**
 * Inline error for a records total, or `''`. The ceiling is reported as soon
 * as it is crossed; the floor only once the user tried to move on
 * (`submitAttempted`), so a half-typed grid is not red from the first keystroke.
 */
export const recordsTotalErrorText = (total, { submitAttempted = false } = {}) => {
    if (total > MAX_TOTAL_RECORDS) return maxRecordsErrorText(MAX_TOTAL_RECORDS);
    if (submitAttempted && !(total >= MIN_TOTAL_RECORDS)) return minRecordsErrorText(MIN_TOTAL_RECORDS);
    return '';
};

/** Per-target counts of a loaded previous configuration (missing targets stay empty). */
const prefillCountsFor = (names, prefillCounts) =>
    Object.fromEntries(
        names.map((name) => [name, Number.isFinite(prefillCounts?.[name]) ? prefillCounts[name] : null])
    );

const prefillStateFor = (names, { counts: prefillCounts, configs: prefillConfigs }) => ({
    counts: prefillCountsFor(names, prefillCounts),
    configs: Object.fromEntries(
        names.map((name) => [name, { ...createDefaultTargetConfig(), ...(prefillConfigs?.[name] || {}) }])
    ),
});

/**
 * State for the "Records to generate" grid + per-target config sections
 * (classification / multi-label / NER step 2). Every target starts empty and
 * holds exactly what the user types; the total is their sum (no default and no
 * rebalancing to a fixed total). Next is blocked while the total is above
 * `MAX_TOTAL_RECORDS` or, once attempted, below `MIN_TOTAL_RECORDS`.
 */
export default function useRecordsState(targetNames) {
    const [counts, setCounts] = useState({});
    const [configs, setConfigs] = useState({});
    const [submitAttempted, setSubmitAttempted] = useState(false);
    // Load-previous prefill waiting for the reseed effect when the target list
    // is changing in the same update (setEditedNames + prefill together).
    const pendingPrefillRef = useRef(null);

    const applyState = (next) => {
        setCounts(next.counts);
        setConfigs(next.configs);
        setSubmitAttempted(false);
    };

    // Targets load asynchronously (usecase / get-label-counts); (re)seed state
    // whenever the target list changes — from a pending prefill when one
    // matches the new list, otherwise empty counts and default configs.
    useEffect(() => {
        const namesKey = targetNames.join('\u0000');
        const pending = pendingPrefillRef.current;
        pendingPrefillRef.current = null;
        if (pending && pending.namesKey === namesKey) {
            applyState(prefillStateFor(targetNames, pending));
            return;
        }
        applyState({
            counts: Object.fromEntries(targetNames.map((name) => [name, null])),
            configs: Object.fromEntries(targetNames.map((name) => [name, createDefaultTargetConfig()])),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetNames.join('\u0000')]);

    /**
     * Apply a loaded previous configuration. `names` is the target list the
     * prefill belongs to: when it differs from the current one (the caller is
     * updating the labels in the same handler), the prefill is applied by the
     * reseed effect once the new list propagates.
     */
    const prefill = ({ names, counts: prefillCounts, configs: prefillConfigs }) => {
        const nextNames = names?.length ? names : targetNames;
        const entry = {
            namesKey: nextNames.join('\u0000'),
            counts: prefillCounts,
            configs: prefillConfigs,
        };
        if (entry.namesKey === targetNames.join('\u0000')) {
            pendingPrefillRef.current = null;
            applyState(prefillStateFor(nextNames, entry));
            return;
        }
        pendingPrefillRef.current = entry;
    };

    const onCountChange = (name, value) => {
        setCounts((prev) => ({ ...prev, [name]: Number.isFinite(value) ? Math.max(0, Math.floor(value)) : null }));
    };

    const onConfigChange = (name, partial) => {
        setConfigs((prev) => ({ ...prev, [name]: { ...prev[name], ...partial } }));
    };

    const totalRecords = sumCounts(counts);
    const totalRecordsError = totalRecords > MAX_TOTAL_RECORDS || totalRecords < MIN_TOTAL_RECORDS;
    const totalRecordsErrorText = recordsTotalErrorText(totalRecords, { submitAttempted });

    /** Call from the step's Next handler: surfaces the floor error and reports whether the total is valid. */
    const validate = () => {
        setSubmitAttempted(true);
        return !totalRecordsError;
    };

    return {
        totalRecords,
        counts,
        configs,
        onCountChange,
        onConfigChange,
        totalRecordsError,
        totalRecordsErrorText,
        validate,
        prefill,
    };
}
